import { geocodingClient } from './geocodingClient';
import {
  buildSupportedCountryAliases,
  buildUsStateAliases,
} from '../../utils/location';

export const TYPED_ADDRESS_RESOLUTION_STATUS = Object.freeze({
  RESOLVED: 'resolved',
  NOT_FOUND: 'not-found',
  COARSE_MATCH: 'coarse-match',
  INVALID_RESULT: 'invalid-result',
});

const PRECISE_GEOCODING_LAYERS = new Set(['address', 'street', 'venue']);

const readText = (value) => (typeof value === 'string' ? value.trim() : '');

const normalizeComparison = (value) => readText(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const addNormalizedAlias = (aliases, value) => {
  const normalized = normalizeComparison(value);
  if (normalized) {
    aliases.add(normalized);
  }
};

const buildAdministrativeSuffixes = (suggestion, queryCountryCode) => {
  const suffixes = new Set();
  const regionAliases = new Set([
    readText(suggestion?.regionName),
    ...buildUsStateAliases(suggestion?.regionName),
  ].map(normalizeComparison).filter(Boolean));
  const countryAliases = new Set(buildSupportedCountryAliases({
    countryCode: suggestion?.countryCode || queryCountryCode,
    countryName: suggestion?.countryName,
  }).map(normalizeComparison).filter(Boolean));

  regionAliases.forEach((regionAlias) => addNormalizedAlias(suffixes, regionAlias));
  countryAliases.forEach((countryAlias) => addNormalizedAlias(suffixes, countryAlias));
  regionAliases.forEach((regionAlias) => {
    countryAliases.forEach((countryAlias) => {
      addNormalizedAlias(suffixes, `${regionAlias} ${countryAlias}`);
    });
  });

  const labelSegments = readText(suggestion?.label)
    .split(',')
    .slice(1)
    .map(readText)
    .filter(Boolean);
  labelSegments.forEach((_, index) => {
    addNormalizedAlias(suffixes, labelSegments.slice(0, index + 1).join(' '));
  });

  return suffixes;
};

export const isUnconfirmedCoarseAddressMatch = (typedAddress, suggestion, queryCountryCode) => {
  const layer = readText(suggestion?.layer).toLowerCase();
  if (PRECISE_GEOCODING_LAYERS.has(layer)) {
    return false;
  }

  const normalizedTypedAddress = normalizeComparison(typedAddress);
  const normalizedSuggestionLabel = normalizeComparison(suggestion?.label);
  if (normalizedTypedAddress && normalizedTypedAddress === normalizedSuggestionLabel) {
    return false;
  }

  const typedSegments = readText(typedAddress)
    .split(',')
    .map(readText)
    .filter(Boolean);
  const typedPrimarySegment = normalizeComparison(typedSegments[0]);
  const matchedPrimarySegment = normalizeComparison(readText(suggestion?.label).split(',')[0]);
  if (!typedPrimarySegment || !matchedPrimarySegment) {
    return true;
  }

  const administrativeSuffixes = buildAdministrativeSuffixes(suggestion, queryCountryCode);
  if (typedPrimarySegment === matchedPrimarySegment) {
    const typedQualifiers = normalizeComparison(typedSegments.slice(1).join(' '));
    return Boolean(typedQualifiers && !administrativeSuffixes.has(typedQualifiers));
  }
  if (!typedPrimarySegment.startsWith(`${matchedPrimarySegment} `)) {
    return true;
  }

  const typedSuffix = normalizeComparison([
    typedPrimarySegment.slice(matchedPrimarySegment.length),
    ...typedSegments.slice(1),
  ].join(' '));
  return !administrativeSuffixes.has(typedSuffix);
};

export async function resolveTypedAddress(query, signal) {
  const text = readText(query?.text);
  if (!text) {
    return {
      status: TYPED_ADDRESS_RESOLUTION_STATUS.NOT_FOUND,
      suggestion: null,
      location: null,
    };
  }

  const lookupQuery = {
    text,
    countryCode: query?.countryCode,
    limit: query?.limit ?? 1,
  };
  const suggestions = signal
    ? await geocodingClient.forwardLookup(lookupQuery, signal)
    : await geocodingClient.forwardLookup(lookupQuery);
  const suggestion = suggestions?.[0] || null;

  if (!suggestion) {
    return {
      status: TYPED_ADDRESS_RESOLUTION_STATUS.NOT_FOUND,
      suggestion: null,
      location: null,
    };
  }

  if (isUnconfirmedCoarseAddressMatch(query?.text, suggestion, query?.countryCode)) {
    return {
      status: TYPED_ADDRESS_RESOLUTION_STATUS.COARSE_MATCH,
      suggestion,
      location: null,
    };
  }

  const location = geocodingClient.toGeocodedLocation(suggestion);
  return {
    status: location
      ? TYPED_ADDRESS_RESOLUTION_STATUS.RESOLVED
      : TYPED_ADDRESS_RESOLUTION_STATUS.INVALID_RESULT,
    suggestion,
    location: location || null,
  };
}
